import React, { useContext, useState, useRef, useCallback, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { IncomeContext } from '../context/IncomeContext';
import { FilterContext } from '../context/FilterContext';
import { ModalContext } from '../context/ModalContext';
import '../styles/Income.scss';

export default function Income() {
    const incomeProviderValues = useContext(IncomeContext);
    const filterContext = useContext(FilterContext);
    const modalProviderValues = useContext(ModalContext);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isFormExpanded, setIsFormExpanded] = useState(false);
    const inputRef = useRef(null);

    const handleAddIncome = () => {
        console.log('Attempting to add income:', incomeProviderValues.newIncome);
        if (incomeProviderValues.newIncome.name && incomeProviderValues.newIncome.amount && incomeProviderValues.newIncome.category) {
            incomeProviderValues.addIncome();
        } else {
            modalProviderValues.showAlert('Please fill in name, amount and category');
        }
    };

    // Close editing on mouse click outside
    const closeEditing = useCallback(() => {
        incomeProviderValues.setEditingIncomeField({ id: null, field: null });
    }, [incomeProviderValues]);

    // Get display date for income (first day of selected period for recurring incomes)
    const getDisplayDate = (income) => {
        const isRecurring = income.is_recurring || (income.frequency && income.frequency === 'regular');
        
        if (isRecurring && filterContext.startDate) {
            // For recurring incomes, show same day of month as received_date but in current period
            const startDate = new Date(filterContext.startDate);
            const receivedDate = new Date(income.received_date);
            const dayOfMonth = receivedDate.getDate();
            
            // Create date with current period's month/year and original day
            return new Date(startDate.getFullYear(), startDate.getMonth(), dayOfMonth);
        } else {
            // For one-time incomes, show actual date
            return new Date(income.received_date);
        }
    };

    // Get effective amount for a recurring income based on current period
    const getEffectiveAmount = (income) => {
        if (!income.amount_changes || income.amount_changes.length === 0) {
            return income.amount;
        }
        
        const referenceDate = filterContext.startDate 
            ? new Date(filterContext.startDate) 
            : new Date();
        
        const applicableChange = income.amount_changes
            .filter(change => new Date(change.effective_date) <= referenceDate)
            .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date))[0];
        
        return applicableChange ? applicableChange.new_amount : income.amount;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                closeEditing();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [closeEditing]);

    return (
        <div className="income-wrapper">
            <div className="income-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="header-income">
                    <h2>INCOME</h2>
                </div>
                <button className="toggle-btn" data-tooltip={isExpanded ? "Collapse" : "Expand"}>
                    <i className="material-icons">{isExpanded ? 'expand_less' : 'expand_more'}</i>
                </button>
            </div>

            {isExpanded && (
                <>
                    {/* Income list */}
                    <div className="income-list">
                        {incomeProviderValues.incomes.length === 0 ? (
                            <div className="empty-state">
                                <i className="material-icons">account_balance_wallet</i>
                                <p>No income added yet</p>
                            </div>
                        ) : (
                            [...incomeProviderValues.incomes]
                            .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
                            .map(income => {
                                const category = incomeProviderValues.incomeCategories.find(cat => cat.id === income.category);
                                const categoryName = category?.name || 'Unknown';
                                
                                return (
                                    <div key={income.id} className="income-card">
                                <div className="income-card-body">
                                    <div className="income-info">
                                        {/* Editable name */}
                                        {incomeProviderValues.editingIncomeField.id === income.id && incomeProviderValues.editingIncomeField.field === 'name' ? (
                                            <input
                                                type="text"
                                                className="edit-income-title"
                                                placeholder="Income name"
                                                ref={inputRef}
                                                value={incomeProviderValues.editIncomeName || ''}
                                                onChange={e => incomeProviderValues.setEditIncomeName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        incomeProviderValues.applyIncomeChanges(income.id, 'name');
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="income-title-container">
                                                <h3 
                                                    className="income-title editable"
                                                    onClick={() => {
                                                        incomeProviderValues.setEditingIncomeField({ id: income.id, field: 'name' });
                                                        incomeProviderValues.setEditIncomeName(income.name);
                                                    }}
                                                    data-tooltip="Edit name"
                                                >
                                                    {income.name}
                                                </h3>
                                                {(income.is_recurring || income.frequency === 'regular') && (
                                                    <span className="recurring-badge">
                                                        <i className="material-icons">repeat</i>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className="income-meta">
                                            {/* Editable date */}
                                            {incomeProviderValues.editingIncomeField.id === income.id && incomeProviderValues.editingIncomeField.field === 'date' ? (
                                                <input
                                                    type="date"
                                                    className="edit-income-date"
                                                    ref={inputRef}
                                                    value={incomeProviderValues.editIncomeDate || ''}
                                                    onChange={e => incomeProviderValues.setEditIncomeDate(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            incomeProviderValues.applyIncomeChanges(income.id, 'date');
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <span 
                                                    className="income-date editable"
                                                    onClick={() => {
                                                        incomeProviderValues.setEditingIncomeField({ id: income.id, field: 'date' });
                                                        incomeProviderValues.setEditIncomeDate(income.received_date);
                                                    }}
                                                    data-tooltip="Edit date"
                                                >
                                                    <i className="material-icons">event</i>
                                                    {getDisplayDate(income).toLocaleDateString('en-US', { 
                                                        year: 'numeric', 
                                                        month: 'short', 
                                                        day: 'numeric' 
                                                    })}
                                                </span>
                                            )}
                                            
                                            {/* Editable source */}
                                            {incomeProviderValues.editingIncomeField.id === income.id && incomeProviderValues.editingIncomeField.field === 'source' ? (
                                                <input
                                                    type="text"
                                                    className="edit-income-source"
                                                    placeholder="Source"
                                                    ref={inputRef}
                                                    value={incomeProviderValues.editIncomeSource || ''}
                                                    onChange={e => incomeProviderValues.setEditIncomeSource(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            incomeProviderValues.applyIncomeChanges(income.id, 'source');
                                                        }
                                                    }}
                                                />
                                            ) : income.source ? (
                                                <span 
                                                    className="income-source editable"
                                                    onClick={() => {
                                                        incomeProviderValues.setEditingIncomeField({ id: income.id, field: 'source' });
                                                        incomeProviderValues.setEditIncomeSource(income.source);
                                                    }}
                                                    data-tooltip="Edit source"
                                                >
                                                    <i className="material-icons">business</i>
                                                    {income.source}
                                                </span>
                                            ) : (
                                                <span 
                                                    className="income-source editable add-source"
                                                    onClick={() => {
                                                        incomeProviderValues.setEditingIncomeField({ id: income.id, field: 'source' });
                                                        incomeProviderValues.setEditIncomeSource('');
                                                    }}
                                                    data-tooltip="Add source"
                                                >
                                                    <i className="material-icons">add</i>
                                                    Add source
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="income-actions">
                                        <div className="income-amount-container">
                                            {/* Editable amount */}
                                            {incomeProviderValues.editingIncomeField.id === income.id && incomeProviderValues.editingIncomeField.field === 'amount' ? (
                                                <CurrencyInput
                                                    className="edit-income-amount"
                                                    placeholder="Amount"
                                                    decimalsLimit={2}
                                                    prefix="€ "
                                                    ref={inputRef}
                                                    value={incomeProviderValues.editIncomeAmount || ''}
                                                    onValueChange={value => incomeProviderValues.setEditIncomeAmount(value || '')}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            incomeProviderValues.applyIncomeChanges(income.id, 'amount');
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div 
                                                    className="income-amount editable"
                                                    onClick={() => {
                                                        const isRecurring = income.is_recurring || (income.frequency && income.frequency === 'regular');
                                                        if (isRecurring) {
                                                            // For recurring incomes, open the price change modal
                                                            modalProviderValues.setSelectedExpenseForPriceChange(income);
                                                            modalProviderValues.setPriceChangeType('income');
                                                            modalProviderValues.setIsModalPriceChangeOpen(true);
                                                        } else {
                                                            // For one-time incomes, use inline editing
                                                            incomeProviderValues.setEditingIncomeField({ id: income.id, field: 'amount' });
                                                            incomeProviderValues.setEditIncomeAmount(income.amount);
                                                        }
                                                    }}
                                                    data-tooltip="Edit amount"
                                                >
                                                    € {parseFloat(getEffectiveAmount(income)).toFixed(2)}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <button
                                            className="delete-btn"
                                            onClick={() => incomeProviderValues.deleteIncome(income.id)}
                                            data-tooltip="Delete income"
                                        >
                                            <i className="material-icons">delete</i>
                                        </button>
                                    </div>
                                </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}

            <div className="add-income-header" onClick={() => setIsFormExpanded(!isFormExpanded)}>
                <h3>Add Income</h3>
                <button className="toggle-btn" data-tooltip={isFormExpanded ? "Collapse" : "Expand"}>
                    <i className="material-icons">{isFormExpanded ? 'expand_less' : 'expand_more'}</i>
                </button>
            </div>
            {isFormExpanded && (
                <div className="add-income-form">
                    <input
                        type="text"
                        className="income-name-input"
                        placeholder="Income name"
                        value={incomeProviderValues.newIncome.name || ''}
                        onChange={(e) => incomeProviderValues.setNewIncome({ ...incomeProviderValues.newIncome, name: e.target.value })}
                    />
                    <CurrencyInput
                        className="income-amount-input"
                        placeholder="Amount"
                        decimalsLimit={2}
                        prefix="€ "
                        value={incomeProviderValues.newIncome.amount || ''}
                        onValueChange={(value) => incomeProviderValues.setNewIncome({ ...incomeProviderValues.newIncome, amount: value || '' })}
                    />
                    <input
                        type="date"
                        value={incomeProviderValues.newIncome.received_date || ''}
                        onChange={(e) => incomeProviderValues.setNewIncome({ ...incomeProviderValues.newIncome, received_date: e.target.value })}
                    />
                    <select
                        className="income-category-select"
                        value={incomeProviderValues.newIncome.category || ''}
                        onChange={(e) => incomeProviderValues.setNewIncome({ ...incomeProviderValues.newIncome, category: e.target.value })}
                    >
                        <option value="">Select category</option>
                        {incomeProviderValues.incomeCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Source (optional)"
                        value={incomeProviderValues.newIncome.source || ''}
                        onChange={(e) => incomeProviderValues.setNewIncome({ ...incomeProviderValues.newIncome, source: e.target.value })}
                    />
                    <select
                        className="income-frequency-select"
                        value={incomeProviderValues.newIncome.frequency || 'once'}
                        onChange={(e) => incomeProviderValues.setNewIncome({
                            ...incomeProviderValues.newIncome,
                            frequency: e.target.value,
                            is_recurring: e.target.value === 'regular'
                        })}
                    >
                        <option value="once">One-time</option>
                        <option value="regular">Regular (Monthly)</option>
                    </select>
                    <button className="add-btn" onClick={handleAddIncome}>
                        <i className="material-icons">add</i> Add Income
                    </button>
                </div>
            )}
            
        </div>
    );
}
